import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Logo from "../components/Logo";
import Particles from "../components/Particles";
import FlyingBirds from "../components/FlyingBirds";
import { LANDING_HERO } from "../lib/constants";

export default function Landing() {
  const nav = useNavigate();
  const parallaxRef = useRef(null);
  // Landing always uses a curated hero (birds over mountains at golden hour)
  const bg = LANDING_HERO;

  useEffect(() => {
    const el = parallaxRef.current;
    if (!el) return;
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * -10;
      const y = (e.clientY / window.innerHeight - 0.5) * -8;
      el.style.transform = `scale(1.06) translate3d(${x}px, ${y}px, 0)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Dark base as fallback in case images fail */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(50,20,45,1) 0%, rgba(10,5,16,1) 70%)",
        }}
        aria-hidden
      />
      {/* Ken-burns + parallax hero */}
      <div
        ref={parallaxRef}
        className="ken-burns fixed inset-0 z-0 transition-transform duration-700 ease-out"
        style={{
          backgroundImage: `url(${bg}), url(/fallback-scene.svg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden
      />
      {/* Dark gradient overlay */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,5,16,0.55) 0%, rgba(10,5,16,0.62) 40%, rgba(10,5,16,0.85) 100%)",
        }}
        aria-hidden
      />
      <FlyingBirds />
      <Particles count={14} />
      <Particles count={14} />

      <div className="relative z-10 min-h-screen flex flex-col justify-center max-w-md mx-auto w-full px-6 py-10">
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-7">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
            className="relative"
          >
            {/* Breathing halo */}
            <motion.div
              className="absolute -inset-8 rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(234,200,138,0.4) 0%, rgba(234,200,138,0) 70%)",
                filter: "blur(24px)",
              }}
              animate={{
                scale: [1, 1.12, 1],
                opacity: [0.45, 0.85, 0.45],
              }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              aria-hidden
            />
            <div className="relative">
              <Logo size={100} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="space-y-2.5"
          >
            <h1 className="font-sacred italic text-6xl sm:text-7xl text-gold-gradient tracking-tight leading-none drop-shadow-[0_0_30px_rgba(234,200,138,0.25)]">
              Narrava
            </h1>

            <p className="font-ui text-[11px] tracking-[0.42em] uppercase text-[#d4af7a]">
              Stories that find you
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.55 }}
            className="max-w-[22rem] flex items-center justify-center gap-3"
          >
            <span className="h-px w-8 bg-gradient-to-r from-transparent to-[#d4af7a]/50" />
            <p className="font-ui text-[11px] tracking-[0.38em] text-[#eac88a] lowercase whitespace-nowrap">
              narrative · aatma · vā
            </p>
            <span className="h-px w-8 bg-gradient-to-l from-transparent to-[#d4af7a]/50" />
          </motion.div>

          <motion.p
            variants={{
              hidden: {},
              visible: {
                transition: { delayChildren: 0.9, staggerChildren: 0.14 },
              },
            }}
            initial="hidden"
            animate="visible"
            className="font-sacred italic text-[#f8ecd4]/90 text-lg sm:text-xl leading-[1.6] max-w-[22rem] pt-3"
          >
            {[
              "Every", "human", "carries", "an", "inner", "narrator.",
              "\n",
              "A", "voice", "—", "deeply", "wise,", "often", "forgotten.",
            ].map((word, i) =>
              word === "\n" ? (
                <br key={i} />
              ) : (
                <motion.span
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 8 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.55, ease: "easeOut" },
                    },
                  }}
                  className="inline-block mr-[0.22em]"
                >
                  {word}
                </motion.span>
              )
            )}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="space-y-3 pb-2"
        >
          <button
            data-testid="begin-listening-btn"
            onClick={() => nav("/auth?mode=signup")}
            className="btn-gold w-full rounded-full py-4 px-6 flex items-center justify-center gap-2"
          >
            <span className="font-ui">Begin Listening</span>
            <ArrowRight size={18} strokeWidth={2} />
          </button>
          <button
            data-testid="already-have-account-btn"
            onClick={() => nav("/auth?mode=login")}
            className="btn-outline-gold w-full rounded-full py-4 px-6 font-ui"
          >
            I already have an account
          </button>
          <p className="text-center text-[11px] tracking-[0.2em] text-[#ac9470]/80 pt-2 font-ui">
            No passwords shared · Private, sacred space
          </p>
        </motion.div>
      </div>
    </div>
  );
}